<template>
  <LoadingSpinner v-if="isLoading" />

  <div v-else-if="!isError">
    <div
      v-if="project"
      :key="project.id"
      class="view-project"
    >
      <ProjectHeader :project="project">
        <template #actions>
          <BaseDropdown
            title="Manage"
            right
            :items="[
              { title: 'Edit', to: { name: 'EditProject', params: { customerId: project.customerId, projectId: project.id } } },
              { title: 'Set to Work In Progress', disabled: project.status == PROJECT_STATUS.IN_PROGRESS || updatingStatus, action: () => setProjectStatus(PROJECT_STATUS.IN_PROGRESS) },
              { title: 'Set to Completed', disabled: project.status == PROJECT_STATUS.COMPLETED || updatingStatus, action: () => setProjectStatus(PROJECT_STATUS.COMPLETED) },
              { title: 'Set to Archived', disabled: project.status == PROJECT_STATUS.ARCHIVED || updatingStatus, action: () => setProjectStatus(PROJECT_STATUS.ARCHIVED) },
              { title: 'Delete', action: () => showProjectDeleteConfirmation = true },
            ]"
          >
            <template #dropdown-header-text>
              <div class="flex items-center gap-x-2">
                <span>Manage</span>
                <span
                  v-if="updatingStatus"
                  class="i-heroicons-arrow-path animate-spin"
                />
                <span
                  v-else
                  class="size-5 text-gray-700 i-mdi-dots-horizontal" aria-hidden="true"
                />
              </div>
            </template>
          </BaseDropdown>
        </template>
      </ProjectHeader>
      <ProjectSubNavigation v-if="isEmployee" :project="project" />

      <div class="project-overview flex flex-col lg:flex-row gap-4 lg:gap-8 mt-4 lg:mt-6">
        <div class="contact w-full lg:max-w-sm flex flex-col gap-4 text-sm bg-secondary/20 p-3 lg:p-4 rounded-md">
          <div>
            <h3 class="text-lg font-semibold leading-6">Client</h3>
            <!-- name  -->
            <div class="flex gap-2 items-center mt-2 lg:mt-3">
              <span class="i-heroicons-user-solid" />
              <span>{{ customer.name }}</span>
            </div>
            <!-- email -->
            <div class="flex gap-2 items-center mt-2">
              <span class="i-heroicons-envelope-solid" />
              <span>{{ customer.email }}</span>
            </div>
            <!-- phone -->
            <div class="flex gap-2 items-center mt-2">
              <span class="i-heroicons-phone-solid" />
              <span>{{ customer.phone }}</span>
            </div>

            <!-- project type -->
            <div v-if="project.type" class="mt-4 lg:mt-5">
              <h3 class="text-lg font-semibold leading-6">Project Type</h3>
              <div class="flex gap-2 items-center mt-2">
                <span class="i-heroicons-tag-solid" />
                <ProjectTypeBadge :type="project.type" />
              </div>
            </div>

            <!-- realtor -->
            <div v-if="isEmployee && project.realtorId && realtor" class="mt-4 lg:mt-5">
              <h3 class="text-lg font-semibold leading-6">Realtor</h3>
              <div class="flex gap-2 items-center mt-2">
                <span class="i-heroicons-user-solid" />
                <span>{{ realtor.name }}</span>
              </div>
            </div>

            <!-- site address -->
            <div v-if="project.address" class="mt-4 lg:mt-5">
              <h3 class="text-lg font-semibold leading-6">Site Address</h3>
              <div class="flex gap-2 items-center mt-2">
                <span class="i-heroicons-map-pin-solid" />
                <span>{{ project.address }}</span>
              </div>
            </div>

            <!-- project description -->
            <div v-if="project.description" class="mt-4 lg:mt-5">
              <h3 class="text-lg font-semibold leading-6">Project Description</h3>
              <span class="text-justify mt-2 block">{{ project.description }}</span>
            </div>
          </div>
        </div>

        <div class="messages w-full lg:flex-1">
          <AppTabs :items="messagesTabs">
            <!-- Comments & Questions -->
            <template #comments>
              <div class="my-3 lg:my-4 px-2 lg:px-4">
                <MessageThread
                  :key="`comments-${project.id}`"
                  ref="comments"
                  class="max-h-[280px] lg:max-h-[360px] overflow-y-auto"
                  :messages="messages"
                  :message-type="MESSAGE_TYPES.COMMENT"
                />

                <MessageForm
                  :key="`new-comments-${project.id}`"
                  :project="project"
                  :message-type="MESSAGE_TYPES.COMMENT"
                  :save-funtion="(value) => createMessage({ message: value, id: 'comments' })"
                />
              </div>
            </template>

            <!-- Progress Log -->
            <template #progressLog>
              <div class="my-3 lg:my-4 px-2 lg:px-4">
                <MessageThread
                  :key="`proress-log-${project.id}`"
                  ref="progressLog"
                  class="max-h-[280px] lg:max-h-[360px] overflow-y-auto"
                  :messages="messages"
                  :message-type="MESSAGE_TYPES.PROGRESS_LOG"
                />

                <MessageForm
                  v-if="isEmployee"
                  :key="`new-progress-log-${project.id}`"
                  :project="project"
                  :message-type="MESSAGE_TYPES.PROGRESS_LOG"
                  :save-funtion="(value) => createMessage({ message: value, id: 'progressLog' })"
                />
              </div>
            </template>

            <!-- Private messages -->
            <template #private>
              <div class="my-3 lg:my-4 px-2 lg:px-4">
                <MessageThread
                  :key="`private-messages-${project.id}`"
                  ref="private"
                  class="max-h-[280px] lg:max-h-[360px] overflow-y-auto"
                  :messages="messages"
                  :message-type="MESSAGE_TYPES.INTERNAL"
                />

                <MessageForm
                  :key="`new-private-messages-${project.id}`"
                  :project="project"
                  :message-type="MESSAGE_TYPES.INTERNAL"
                  :save-funtion="(value) => createMessage({ message: value, id: 'private' })"
                />
              </div>
            </template>
          </AppTabs>
        </div>
      </div>
    </div>
  </div>

  <EmptyState v-else>
    <template #icon>
      <span class="i-heroicons-exclamation-circle text-red-500 size-8"></span>
    </template>
    <template #title>
      <h2 class="text-lg font-semibold text-center text-slate-900">Error</h2>
    </template>
    <template #description>
      <p class="text-sm text-center text-slate-500 pt-4">
        An error occurred while trying to load the project
      </p>
    </template>
  </EmptyState>

  <Modal
    id="delete-project-confirmation-modal"
    v-model="showProjectDeleteConfirmation"
    title="Delete Project"
    :no-escape="true"
    :no-backdrop="true"
    :no-close-button="true"
  >
    <template #body>
      <div class="mt-2">
        <p class="text-base mb-3">Are you sure you want to delete this Project?</p>
        <p class="text-sm mb-2">This will also delete:</p>
        <ul class="text-sm list-disc ml-6">
          <li>The budget (if it exists)</li>
          <li>The proposal (if it exists)</li>
          <li>All work orders</li>
          <li>All purchase orders</li>
          <li>All invoices</li>
          <li>All bills</li>
        </ul>
      </div>
    </template>

    <template #footer>
      <div class="bg-white py-3 flex px-6 justify-end gap-2">
        <BaseButton
          text="Cancel"
          :disabled="isDeleting"
          :class="{ 'cursor-not-allowed opacity-50': isDeleting }"
          @click="showProjectDeleteConfirmation = false"
        />

        <BaseButton
          variant="danger"
          :disabled="isDeleting"
          :class="{ 'cursor-not-allowed opacity-50': isDeleting }"
          @click="deleteProject()"
        >
          <div class="flex justify-between items-center gap-2">
            <span>Confirm</span>
            <span
              v-if="isDeleting"
              class="i-heroicons-arrow-path animate-spin"
            />
          </div>
        </BaseButton>
      </div>
    </template>
  </Modal>
</template>

<script lang="ts" setup>
import { Message, MESSAGE_TYPES } from '@/models/message'
import MessageForm from '../components/ProjectMessage.vue'
import MessageThread from '../components/ProjectMessageThread.vue'
import ProjectHeader from '@/components/project/ProjectHeader.vue'
import ProjectSubNavigation from '@/components/project/ProjectSubNavigation.vue'
import { useHead } from '@unhead/vue'
import { useRoute, useRouter } from 'vue-router'
import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query'
import { useAuth } from '@/composables/useAuth'
import ProjectService from '@/services/ProjectService'
import CustomerService from '@/services/CustomerService'
import MessageService from '@/services/MessageService'
import { computed, Ref, useTemplateRef } from 'vue'
import AppTabs, { TabItem } from '@/components/app/AppTabs.vue'
import { ref } from 'vue'
import LoadingSpinner from '@/components/LoadingSpinner.vue'
import { useNotification } from '@kyvg/vue3-notification'
import EmptyState from '@/components/app/EmptyState.vue'
import BaseButton from '@/components/base/BaseButton.vue'
import BaseDropdown from '@/components/base/BaseDropdown.vue'
import Modal from '@/components/app/AppModal.vue'
import { Project, PROJECT_STATUS } from '@/models/project'
import ProjectTypeBadge from '@/components/project/ProjectTypeBadge.vue'

useHead({
  title: 'Project Overview'
})

const $route = useRoute()
const $router = useRouter()
const projectId = $route.params.projectId as string
const customerId = $route.params.customerId as string
const { isEmployee } = useAuth()
const queryClient = useQueryClient()
const { notify } = useNotification()
const isError = computed(() => projectError.value || customerError.value)
const showProjectDeleteConfirmation = ref(false)

const projectService = new ProjectService()
const { data: project, isLoading: projectLoading, error: projectError } = useQuery({
  queryKey: ['project', projectId],
  queryFn: () => projectService.find(projectId),
  enabled: !!projectId
})
const {data: customer, isLoading: customerLoading, error: customerError } = useQuery({
  queryKey: ['customer', customerId],
  queryFn: () => new CustomerService().find(customerId),
  enabled: !!customerId
})

const messageService = new MessageService(`customers/${customerId}/projects/${projectId}`)
const { data: messages, isLoading: messagesLoading } = useQuery({
  queryKey: ['messages', customerId, projectId],
  queryFn: () => messageService.getByProject(projectId),
  enabled: !!customerId && !!projectId
})

const messagesTabs: Ref<TabItem[]> = ref([
  { label: 'Comments & Questions', slot: 'comments' },
  { label: 'Progress Log', slot: 'progressLog' },
  { label: 'Private', slot: 'private' }
])

const { data: realtor, isLoading: realtorLoading } = useQuery({
  queryKey: ['customer', project.value?.realtorId],
  queryFn: () => new CustomerService().find(project.value?.realtorId),
  enabled: !!project.value?.realtorId
})

const isLoading = computed(() => projectLoading.value || customerLoading.value || messagesLoading.value || realtorLoading.value)

const commentsRef = useTemplateRef('comments')
const progressLogRef = useTemplateRef('progressLog')
const privateRef = useTemplateRef('private')

const { mutateAsync: createMessage} = useMutation({
  mutationFn: async (payload: {message: Message; id: string}) => {
    // const newMessage = await messageService.create(message)
    const newMessage = await projectService.addMessageToProject(project.value.id, project.value.customerId, payload.message)
    return { newMessage, id: payload.id }
  },
  onSuccess: ({ newMessage, id }) => {
    queryClient.setQueryData(['messages', customerId, projectId], [...messages.value, newMessage])
    notify({
      type: 'success',
      title: 'Message created',
      text: 'Message created successfully'
    })

    if (id === 'comments') {
      scrollMessagesToBottom(commentsRef.value.$el)
    } else if (id === 'progressLog') {
      scrollMessagesToBottom(progressLogRef.value.$el)
    } else if (id === 'private') {
      scrollMessagesToBottom(privateRef.value.$el)
    }

  },
  onError: (error) => {
    console.error(error)
    notify({
      type: 'error',
      title: 'Error',
      text: 'An error occurred while trying to create the message'
    })
  }
})

const scrollMessagesToBottom = (el: HTMLElement) => {
  setTimeout(() => {
    if (el) {
      el.scrollTo({
        top: el.scrollHeight,
        behavior: 'smooth'
      })
    }
  }, 100)
}

const { mutateAsync: deleteProject, isPending: isDeleting } = useMutation({
  mutationFn: () => projectService.deleteProject(projectId, customerId),
  onSuccess: () => {
    queryClient.removeQueries({ queryKey: ['project', projectId] })
    queryClient.invalidateQueries()
    notify({
      type: 'success',
      title: 'Project deleted',
      text: 'Project deleted successfully'
    })
    queryClient.invalidateQueries({ queryKey: ['projects'] })
    queryClient.invalidateQueries({ queryKey: ['project', projectId] })
    showProjectDeleteConfirmation.value = false
    $router.push({ name: 'ListProjects' })
  },
  onError: (error) => {
    notify({
      type: 'error',
      title: 'Error deleting project',
      text: error.message || 'An error occurred while trying to delete the project'
    })
  }
})

const { mutateAsync:setProjectStatus, isPending:updatingStatus } = useMutation({
  mutationFn: async (status: string) => {
    return await new CustomerService().updateProject(customerId, project.value.id, {
      ... project.value,
      status
    })
  },
  onSuccess: (updatedProject: Project) => {
    queryClient.setQueryData(['project', projectId], updatedProject)
    queryClient.invalidateQueries({ queryKey: ['projects'] })
    notify({
      type: 'success',
      title: 'Project updated',
      text: 'Project status updated successfully'
    })
  },
  onError: (error) => {
    notify({
      type: 'error',
      title: 'Error updating project',
      text: error.message || 'An error occurred while trying to update the project'
    })
  }
})

</script>
